import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link2, X } from 'lucide-react'

import { dealPartyService, type DealParty, type DealPartyEdit } from '@/services/dealParties'
import { Field } from './fields'
import { detailOf } from './errors'
import { usePartyDialogFocus } from './usePartyDialogFocus'

/**
 * Correct one party on a deal.
 *
 * Client: "i need everything to be editable… there is a great deal of data
 * that is populating under each that isn't correct that i need to edit."
 *
 * Nothing on this card was editable before beyond the party's NAME, and the
 * name was the one field that was usually right — it comes off the deal record
 * somebody typed. What was wrong was everything underneath it, all of which
 * lives on the CRM contact and none of which this screen could reach.
 *
 * Two destinations, one save:
 *
 *   * the NAME goes to the deal's own columns, with lineage, because that is
 *     what the pipeline grid and every export read;
 *   * everything else goes to the CRM contact through the CRM's own edit path,
 *     so the same correction shows on the directory screen.
 *
 * A field left blank is submitted as blank — that is how a wrong value gets
 * cleared. Absent fields are what the server leaves alone, and this dialog
 * shows every field it submits.
 */
export function EditPartyDialog({
  loanId,
  party,
  onClose,
  onSaved,
}: {
  loanId: string
  party: DealParty
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  usePartyDialogFocus('Edit party')
  const [name, setName] = useState(party.name)
  const [company, setCompany] = useState(party.company ?? '')
  const [title, setTitle] = useState(party.title ?? '')
  const [email, setEmail] = useState(party.email ?? '')
  const [phone, setPhone] = useState(party.phones.primary ?? '')
  const [directPhone, setDirectPhone] = useState(party.phones.direct ?? '')
  const [mobilePhone, setMobilePhone] = useState(party.phones.mobile ?? '')
  const [officePhone, setOfficePhone] = useState(party.phones.office ?? '')
  // A website only shown because it was derived from an email domain is not a
  // recorded value, so the field opens empty rather than inviting somebody to
  // "confirm" a claim nobody made.
  const [website, setWebsite] = useState(party.websiteBasis === 'recorded' ? (party.website ?? '') : '')
  const [linkedIn, setLinkedIn] = useState(party.linkedIn ?? '')
  const [address, setAddress] = useState(party.address ?? '')
  const [notes, setNotes] = useState(party.notes ?? '')
  const [isEntity, setIsEntity] = useState(party.isEntity)
  const [error, setError] = useState<string | null>(null)

  const [city, state] = (party.location ?? '').split(',').map((part) => part.trim())
  const [cityValue, setCityValue] = useState(city ?? '')
  const [stateValue, setStateValue] = useState(state ?? '')

  const linked = Boolean(party.contactId)
  const nameOnly = !linked

  const save = useMutation({
    mutationFn: () => {
      const patch: DealPartyEdit = { name: name.trim() }
      if (linked) {
        Object.assign(patch, {
          company: company.trim(),
          title: title.trim(),
          email: email.trim(),
          phone: phone.trim(),
          directPhone: directPhone.trim(),
          mobilePhone: mobilePhone.trim(),
          officePhone: officePhone.trim(),
          website: website.trim(),
          linkedIn: linkedIn.trim(),
          address: address.trim(),
          city: cityValue.trim(),
          state: stateValue.trim(),
          notes: notes.trim(),
          isEntity,
        })
      }
      return party.alsoSlots?.length
        ? dealPartyService.edit(loanId, party.id, patch, party.contactId, party.alsoSlots)
        : dealPartyService.edit(loanId, party.id, patch, party.contactId)
    },
    onSuccess: () => onSaved(),
    onError: (failure) => setError(detailOf(failure, 'The party could not be saved.')),
  })

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Edit party"
    >
      <div className="card flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-4 py-2.5">
          <span className="terminal-kicker">Edit {party.kind}</span>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-500 hover:text-stone-200">
            <X size={14} />
          </button>
        </div>

        <form
          className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            if (name.trim()) save.mutate()
          }}
        >
          {linked ? (
            <p className="flex items-start gap-1.5 rounded border border-amber-400/15 bg-amber-400/[0.05] px-2.5 py-1.5 text-[10px] leading-relaxed text-amber-200/90">
              <Link2 size={11} className="mt-0.5 shrink-0" />
              These fields are the CRM contact behind this party. A correction here shows on the CRM
              directory, and one made there shows here.
            </p>
          ) : (
            <p className="rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[10px] leading-relaxed text-stone-400">
              No CRM contact is behind this party yet, so only the name — which is a field on the deal
              record — can be corrected. Add them as a party to record contact details.
            </p>
          )}

          <Field label="Name" required value={name} onChange={setName} autoFocus />
          {party.dealFields.length > 0 && (
            <p className="-mt-2 text-[9px] text-stone-600">
              Writes the deal’s {party.dealFields.join(' and ')} field{party.dealFields.length > 1 ? 's' : ''}.
            </p>
          )}

          {!nameOnly && (
            <>
              <Field label={isEntity ? 'Entity name' : 'Firm'} value={company} onChange={setCompany} />
              <Field label={isEntity ? 'Entity type' : 'Title'} value={title} onChange={setTitle} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Email" value={email} onChange={setEmail} type="email" />
                <Field label="Phone" value={phone} onChange={setPhone} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Direct" value={directPhone} onChange={setDirectPhone} />
                <Field label="Mobile" value={mobilePhone} onChange={setMobilePhone} />
                <Field label="Office" value={officePhone} onChange={setOfficePhone} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Website"
                  value={website}
                  onChange={setWebsite}
                  placeholder={party.companyDomain ?? 'hollis-cap.com'}
                />
                <Field label="LinkedIn" value={linkedIn} onChange={setLinkedIn} />
              </div>
              {party.websiteBasis === 'email_domain' && (
                <p className="-mt-2 text-[9px] text-stone-600">
                  The card currently shows <span className="text-stone-400">{party.website}</span>, taken from
                  the addresses on record. Enter a site to replace it.
                </p>
              )}
              <Field label="Address" value={address} onChange={setAddress} />
              <div className="grid grid-cols-[1fr_100px] gap-2">
                <Field label="City" value={cityValue} onChange={setCityValue} />
                <Field label="State" value={stateValue} onChange={setStateValue} />
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-stone-300">
                <input
                  type="checkbox"
                  checked={isEntity}
                  onChange={(e) => setIsEntity(e.target.checked)}
                  className="accent-amber-400"
                />
                This is an organization, not a person
              </label>
              <Field label="Notes" value={notes} onChange={setNotes} />
            </>
          )}

          {error && <p className="text-[11px] text-red-300">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary text-[11px]">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || save.isPending}
              className="rounded-md border border-amber-400/20 bg-amber-400/[0.08] px-3 py-1 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.14] disabled:cursor-not-allowed disabled:text-stone-600"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
